/**
 * When the ROOT loader re-runs (root.tsx's shouldRevalidate).
 *
 * Plain navigation inside one area never re-runs it: it reads the tenant and
 * the viewer, and every page change re-reading them is waste. It re-runs
 *
 *   - after any form action: the client's provider map comes from it, and an
 *     admin who added a photographer in Settings and clicked Schedule hit
 *     "static data for provider … could not be found" until a hard reload
 *     (review of #278). Sign-in is a form action too, and the redirect chain
 *     it starts carries the submission, so the office's search key arrives;
 *   - when the navigation crosses from one top-level area to another
 *     (/vendor → /logout → / → /login, /login → /vendor, /vendor →
 *     /provider/…). Signing out is plain navigation — a link to /logout, then
 *     `navigate('/')` — and the viewer only changes at such a crossing. Root
 *     data is viewer-dependent (the office's search key, the office's
 *     provider home bases), so a signed-out page must not keep the office's.
 *     It is page-dependent too: the search key exists on /vendor pages only,
 *     so an office user who goes /vendor → /customer/… drops it and gets it
 *     back on the way into /vendor.
 */
export type RootRevalidateArgs = {
  formAction?: string;
  currentUrl: URL;
  nextUrl: URL;
};

/** The first path segment, lower-cased: '' for `/`, 'vendor' for `/vendor/schedule`. */
export function root_area(pathname: string): string {
  return (pathname.split('/')[1] ?? '').toLowerCase();
}

export function root_should_revalidate({ formAction, currentUrl, nextUrl }: RootRevalidateArgs): boolean {
  return Boolean(formAction) || root_area(currentUrl.pathname) !== root_area(nextUrl.pathname);
}
